import {
  collection,
  query,
  where,
  orderBy,
  getDocs,
  Timestamp,
  onSnapshot,
  doc,
  getDoc,
  addDoc,
  updateDoc,
  deleteDoc,
  serverTimestamp,
  runTransaction
} from 'firebase/firestore';
import { db } from '../firebase';
import { generateExpenseGLEntries, deleteGLTransaction, getGLTransactions } from './glTransactionServices';

export type ExpenseStatus = 'pending' | 'paid' | 'cancelled';
export type PaymentMethod = 'Bank Transfer' | 'Credit Card' | 'Cash' | 'Direct Debit' | 'Other';

export interface Expense {
  id?: string;
  title: string;
  amount: number;
  category: string;
  method: string;
  status: string;
  date: Date | Timestamp;
  description?: string;
  vendor?: string;
  vendorName?: string;
  vendorContact?: string;
  receiptUrl?: string;
  createdBy?: string;
  createdAt?: Date | Timestamp;
  updatedAt?: Date | Timestamp;
}

// Convert Firestore timestamp to Date
export const convertTimestampToDate = (timestamp: any): Date => {
  if (timestamp?.toDate) {
    return timestamp.toDate();
  }
  return new Date(timestamp || Date.now());
};

// Get expenses with optional date range filtering
export const getExpenses = async (
  startDate?: Date | null, 
  endDate?: Date | null
): Promise<Expense[]> => {
  try {
    let expensesQuery = query(
      collection(db, 'expenses'),
      orderBy('date', 'desc')
    );

    if (startDate) {
      const startTimestamp = Timestamp.fromDate(new Date(startDate));
      expensesQuery = query(
        expensesQuery,
        where('date', '>=', startTimestamp)
      );
    }

    if (endDate) {
      const endTimestamp = Timestamp.fromDate(new Date(endDate));
      expensesQuery = query(
        expensesQuery,
        where('date', '<=', endTimestamp)
      );
    }

    const querySnapshot = await getDocs(expensesQuery);
    
    return querySnapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        ...data,
        date: convertTimestampToDate(data.date),
        createdAt: data.createdAt ? convertTimestampToDate(data.createdAt) : undefined,
        updatedAt: data.updatedAt ? convertTimestampToDate(data.updatedAt) : undefined
      } as Expense;
    });
  } catch (error) {
    console.error('Error getting expenses:', error);
    throw error;
  }
};

// Get a single expense by ID
export const getExpenseById = async (id: string): Promise<Expense | null> => {
  try {
    const docRef = doc(db, 'expenses', id);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      const data = docSnap.data();
      return {
        id: docSnap.id,
        ...data,
        date: convertTimestampToDate(data.date),
        createdAt: data.createdAt ? convertTimestampToDate(data.createdAt) : undefined,
        updatedAt: data.updatedAt ? convertTimestampToDate(data.updatedAt) : undefined
      } as Expense;
    }
    
    return null;
  } catch (error) {
    console.error('Error getting expense by ID:', error);
    throw error;
  }
};

// Create a new expense with GL transaction
export const createExpense = async (expense: Omit<Expense, 'id'>): Promise<string> => {
  try {
    const expenseData = {
      ...expense,
      date: expense.date instanceof Date ? Timestamp.fromDate(expense.date) : Timestamp.fromDate(new Date()),
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };
    
    // Use a transaction to ensure both the expense and GL entries are created
    let expenseId = '';
    
    await runTransaction(db, async (transaction) => {
      // Create the expense
      const expenseRef = doc(collection(db, 'expenses'));
      transaction.set(expenseRef, expenseData);
      expenseId = expenseRef.id;
      
      // Create GL transaction entries
      try {
        await generateExpenseGLEntries(
          expense.amount,
          expense.date,
          expenseRef.id,
          expense.title,
          expense.category,
          expense.method
        );
      } catch (glError) {
        console.error('Error creating GL entries:', glError);
        // Continue with expense creation even if GL entries fail
        // This ensures the expense is still recorded
      }
    });
    
    return expenseId;
  } catch (error) {
    console.error('Error creating expense:', error);
    throw error;
  }
};

// Update an existing expense and related GL transactions
export const updateExpense = async (id: string, expense: Partial<Expense>): Promise<void> => {
  try {
    const expenseData = {
      ...expense,
      updatedAt: serverTimestamp()
    };
    
    // Convert date to Timestamp if it exists
    if (expense.date) {
      expenseData.date = expense.date instanceof Date ? Timestamp.fromDate(expense.date) : Timestamp.fromDate(new Date());
    }
    
    // Get the existing expense to check if amount or category changed
    const existingExpense = await getExpenseById(id);
    
    if (!existingExpense) {
      throw new Error('Expense not found');
    }
    
    // Update the expense document
    const docRef = doc(db, 'expenses', id);
    await updateDoc(docRef, expenseData);
    
    // If amount or category changed, update GL transactions
    if (expense.amount !== undefined && expense.amount !== existingExpense.amount ||
        expense.category !== undefined && expense.category !== existingExpense.category) {
      
      // Find existing GL transactions for this expense
      const existingTransactions = await getGLTransactions(null, null, 'expense', id);
      
      // Delete existing GL transactions
      for (const transaction of existingTransactions) {
        if (transaction.id) {
          await deleteGLTransaction(transaction.id);
        }
      }
      
      // Create new GL transaction with updated data
      try {
        await generateExpenseGLEntries(
          expense.amount !== undefined ? expense.amount : existingExpense.amount,
          expense.date !== undefined ? expense.date : existingExpense.date,
          id,
          expense.title !== undefined ? expense.title : existingExpense.title,
          expense.category !== undefined ? expense.category : existingExpense.category,
          expense.method !== undefined ? expense.method : existingExpense.method
        );
      } catch (glError) {
        console.error('Error updating GL entries:', glError);
        // Continue even if GL entries fail
      }
    }
  } catch (error) {
    console.error('Error updating expense:', error);
    throw error;
  }
};

// Delete an expense and its GL transactions
export const deleteExpense = async (id: string): Promise<void> => {
  try {
    // Find existing GL transactions for this expense
    const existingTransactions = await getGLTransactions(null, null, 'expense', id);
    
    // Delete the expense document
    const docRef = doc(db, 'expenses', id);
    await deleteDoc(docRef);
    
    // Delete associated GL transactions
    for (const transaction of existingTransactions) {
      if (transaction.id) {
        await deleteGLTransaction(transaction.id);
      }
    }
  } catch (error) {
    console.error('Error deleting expense:', error);
    throw error;
  }
};

// Subscribe to expenses with real-time updates
export const subscribeToExpenses = (
  callback: (expenses: Expense[]) => void,
  startDate?: Date | null,
  endDate?: Date | null
) => {
  let expensesQuery = query(
    collection(db, 'expenses'),
    orderBy('date', 'desc')
  );

  if (startDate) {
    const startTimestamp = Timestamp.fromDate(new Date(startDate));
    expensesQuery = query(
      expensesQuery,
      where('date', '>=', startTimestamp)
    );
  }

  if (endDate) {
    const endTimestamp = Timestamp.fromDate(new Date(endDate));
    expensesQuery = query(
      expensesQuery,
      where('date', '<=', endTimestamp)
    );
  }

  return onSnapshot(expensesQuery, (querySnapshot) => {
    const expenses = querySnapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        ...data,
        date: convertTimestampToDate(data.date),
        createdAt: data.createdAt ? convertTimestampToDate(data.createdAt) : undefined,
        updatedAt: data.updatedAt ? convertTimestampToDate(data.updatedAt) : undefined
      } as Expense;
    });
    
    callback(expenses);
  });
};

// Get expenses by category
export const getExpensesByCategory = async (
  startDate?: Date, 
  endDate?: Date
): Promise<{category: string, amount: number}[]> => {
  try {
    const expenses = await getExpenses(startDate, endDate);
    
    // Group expenses by category and sum amounts
    const categoryMap = new Map<string, number>();
    
    expenses.forEach(expense => {
      if (expense.status !== 'cancelled') {
        const currentAmount = categoryMap.get(expense.category) || 0;
        categoryMap.set(expense.category, currentAmount + expense.amount);
      }
    });
    
    // Convert map to array of objects
    return Array.from(categoryMap.entries()).map(([category, amount]) => ({
      category,
      amount
    }));
  } catch (error) {
    console.error('Error getting expenses by category:', error);
    throw error;
  }
};

// Get expenses by payment method
export const getExpensesByPaymentMethod = async (
  startDate?: Date, 
  endDate?: Date
): Promise<{method: string, amount: number}[]> => {
  try {
    const expenses = await getExpenses(startDate, endDate);
    
    // Group expenses by payment method and sum amounts
    const methodMap = new Map<string, number>();
    
    expenses.forEach(expense => {
      if (expense.status !== 'cancelled') {
        const currentAmount = methodMap.get(expense.method) || 0;
        methodMap.set(expense.method, currentAmount + expense.amount);
      }
    });
    
    // Convert map to array of objects
    return Array.from(methodMap.entries()).map(([method, amount]) => ({
      method,
      amount
    }));
  } catch (error) {
    console.error('Error getting expenses by payment method:', error);
    throw error;
  }
};
